import { useCallback, useEffect, useState } from "react";
import { IOSInstallBanner } from "../components/IOSInstallBanner";

const API = "/api"; // same-origin — the ingress/worker proxies /api to the backend

const fmtUsd = (n) =>
  n == null
    ? "—"
    : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ShopItem = ({ item, onBuy, busy }) => {
  const [view, setView] = useState("front"); // front | back | court
  const [size, setSize] = useState(item.sizes[1] || item.sizes[0]);
  const views = [
    { key: "front", label: "Front", src: item.image },
    ...(item.image_back ? [{ key: "back", label: "Back", src: item.image_back }] : []),
    ...(item.image_lifestyle ? [{ key: "court", label: "On Court", src: item.image_lifestyle }] : []),
  ];
  const active = views.find((v) => v.key === view) || views[0];
  return (
    <div
      data-testid={`shop-item-${item.item_id}`}
      className="rounded-xl border border-stone-800 bg-stone-900/60 p-4 space-y-3 flex flex-col"
    >
      <div className="aspect-square rounded-lg border border-stone-800 relative overflow-hidden group bg-white">
        <img
          src={active.src}
          alt={item.name}
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-transparent to-transparent pointer-events-none"></div>
        {views.length > 1 && (
          <div className="absolute top-3 right-3 flex gap-1" data-testid={`shop-item-${item.item_id}-views`}>
            {views.map((v) => (
              <button
                key={v.key}
                type="button"
                data-testid={`shop-item-${item.item_id}-view-${v.key}`}
                onClick={() => setView(v.key)}
                className={`text-[10px] mono-tag uppercase tracking-widest px-2.5 py-1.5 rounded-md border transition-colors ${
                  view === v.key
                    ? "bg-amber-500 border-amber-500 text-stone-950"
                    : "bg-stone-950/80 border-amber-500/40 text-amber-400 hover:bg-amber-500 hover:text-stone-950"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        )}
        <p className="absolute bottom-0 left-0 right-0 p-4 text-[11px] text-stone-200 font-medium">
          {item.name}
        </p>
      </div>
      <div className="flex items-center justify-between">
        <span className="mono-tag text-xs text-amber-500">{fmtUsd(item.price_usd)} sUSD</span>
        <div className="flex gap-1" data-testid={`shop-item-${item.item_id}-sizes`}>
          {item.sizes.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSize(s)}
              className={`text-[10px] mono-tag px-2 py-1 rounded border transition-colors ${
                size === s
                  ? "border-amber-500 bg-amber-500 text-stone-950"
                  : "border-stone-700 text-stone-400 hover:border-amber-500/60 hover:text-amber-400"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>
      <button
        type="button"
        data-testid={`shop-item-${item.item_id}-buy`}
        disabled={busy}
        onClick={() => onBuy(item, size)}
        className="w-full mt-auto mono-tag text-xs uppercase tracking-widest bg-amber-500 text-stone-950 font-bold py-3 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
      >
        {busy ? "Opening checkout…" : "Buy with sUSD"}
      </button>
    </div>
  );
};

export default function Shop() {
  const [catalog, setCatalog] = useState([]);
  const [catalogError, setCatalogError] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem("wallet_token") || "");
  const [me, setMe] = useState(null);
  const [loginForm, setLoginForm] = useState({ ledger_uuid: "", phone: "" });
  const [loginError, setLoginError] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [busyItem, setBusyItem] = useState("");
  const [orderError, setOrderError] = useState("");
  const [placed, setPlaced] = useState(null); // last order + pay_url
  const [orders, setOrders] = useState([]);

  const loadCatalog = useCallback(async () => {
    try {
      const res = await fetch(`${API}/shop/catalog`);
      if (!res.ok) throw new Error("catalog unavailable");
      setCatalog((await res.json()).items || []);
    } catch {
      setCatalogError("The catalog could not be loaded — the exchange may be mid-restart.");
    }
  }, []);

  const loadOrders = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API}/shop/orders`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401) {
        sessionStorage.removeItem("wallet_token");
        setToken("");
        return;
      }
      if (res.ok) setOrders((await res.json()).orders || []);
      const meRes = await fetch(`${API}/wallet/me`, { headers: { Authorization: `Bearer ${token}` } });
      if (meRes.ok) setMe(await meRes.json());
    } catch {
      /* order list non-blocking */
    }
  }, [token]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    loadOrders();
    const t = setInterval(loadOrders, 15000);
    return () => clearInterval(t);
  }, [loadOrders]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/wallet/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) {
        const d = data?.detail;
        const msg = typeof d === "string" ? d
          : Array.isArray(d) ? d.map((x) => x?.msg || JSON.stringify(x)).join("; ")
          : (d?.message || d?.code) || "Sign-in failed";
        throw new Error(msg);
      }
      sessionStorage.setItem("wallet_token", data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const buy = async (item, size) => {
    if (!token) {
      setOrderError("Sign in with your member wallet below, then tap Buy again.");
      document.getElementById("shop-signin")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setBusyItem(item.item_id);
    setOrderError("");
    setPlaced(null);
    try {
      const res = await fetch(`${API}/shop/orders`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ item_id: item.item_id, size }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Checkout failed");
      setPlaced(data);
      loadOrders();
      // hand off to the member wallet — the Face ID approval settles the request
      window.location.href = data.pay_url;
    } catch (err) {
      setOrderError(err.message);
    } finally {
      setBusyItem("");
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 text-stone-200">
      <header className="border-b border-stone-800 bg-[#0a1128]/80">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <a href="/" data-testid="shop-home-link" className="flex items-center gap-3">
            <img
              src="/assets/bgnzn-logo.png"
              alt="BGNZN"
              className="h-10 w-auto invert hue-rotate-180"
            />
            <span className="display-serif font-bold text-stone-100 hidden sm:inline">
              BGNZN Apparel
            </span>
          </a>
          <a
            href="/wallet"
            data-testid="shop-wallet-link"
            className="mono-tag text-xs uppercase tracking-widest text-amber-500 border border-amber-500/40 rounded-lg px-4 py-2 hover:bg-amber-500 hover:text-stone-950 transition-colors"
          >
            My Wallet
          </a>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-12 space-y-12">
        <div className="max-w-md">
          <IOSInstallBanner label="Shop from your iPhone home screen" />
        </div>
        <div className="space-y-3 max-w-2xl">
          <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
            The House Line · Members Only Checkout
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-stone-100 display-serif">
            BGNZN Apparel Storefront
          </h1>
          <p className="text-stone-400 text-sm">
            Every piece settles in $sUSD on the private exchange — approve with Face ID from your
            member wallet. No cards, no gateways, nothing leaves the loop.
          </p>
        </div>

        {catalogError && (
          <p data-testid="shop-catalog-error" className="text-red-400 text-sm">{catalogError}</p>
        )}
        {orderError && (
          <p data-testid="shop-order-error" className="text-amber-400 text-sm">{orderError}</p>
        )}
        {placed && (
          <div
            data-testid="shop-order-placed"
            className="rounded-xl border border-amber-500/40 bg-stone-900/80 p-5 space-y-2"
          >
            <p className="mono-tag text-xs uppercase tracking-widest text-amber-500">
              Order {placed.order_id} — awaiting your Face ID approval
            </p>
            <p className="text-stone-300 text-sm">
              {placed.item_name} ({placed.size}) — {fmtUsd(placed.price_usd)} sUSD. Complete it in
              your wallet.
            </p>
            <a
              data-testid="shop-order-pay-link"
              href={placed.pay_url}
              className="inline-block mono-tag text-xs uppercase tracking-widest bg-amber-500 text-stone-950 font-bold px-4 py-2 rounded-lg hover:bg-amber-400 transition-colors"
            >
              Open wallet to pay
            </a>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="shop-catalog-grid">
          {catalog.map((item) => (
            <ShopItem key={item.item_id} item={item} onBuy={buy} busy={busyItem === item.item_id} />
          ))}
        </div>

        {!token && (
          <div
            id="shop-signin"
            data-testid="shop-signin-panel"
            className="rounded-xl border border-stone-800 bg-stone-900/60 p-6 max-w-md space-y-4"
          >
            <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
              Member Sign-In
            </span>
            <p className="text-stone-400 text-sm">
              Checkout runs through your member wallet. Enter your ledger UUID and phone — the same
              sign-in as the wallet app.
            </p>
            <form onSubmit={handleLogin} className="space-y-3">
              <input
                data-testid="shop-login-uuid"
                type="text"
                required
                placeholder="Ledger UUID"
                value={loginForm.ledger_uuid}
                onChange={(e) => setLoginForm({ ...loginForm, ledger_uuid: e.target.value })}
                className="w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-sm text-stone-200 focus:border-amber-500 focus:outline-none"
              />
              <input
                data-testid="shop-login-phone"
                type="tel"
                required
                placeholder="Phone number"
                value={loginForm.phone}
                onChange={(e) => setLoginForm({ ...loginForm, phone: e.target.value })}
                className="w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-sm text-stone-200 focus:border-amber-500 focus:outline-none"
              />
              {loginError && (
                <p data-testid="shop-login-error" className="text-red-400 text-xs">{loginError}</p>
              )}
              <button
                data-testid="shop-login-submit"
                type="submit"
                disabled={loginBusy}
                className="w-full mono-tag text-xs uppercase tracking-widest bg-amber-500 text-stone-950 font-bold py-3 rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-50"
              >
                {loginBusy ? "Signing in…" : "Sign in to shop"}
              </button>
            </form>
            <p className="text-stone-500 text-[11px]">
              No wallet yet? <a href="/guest" className="text-amber-500 hover:underline">Open one at the guest desk</a>.
            </p>
          </div>
        )}

        {token && (
          <div data-testid="shop-orders-panel" className="space-y-4 pt-6 border-t border-stone-800/60">
            <div className="flex items-end justify-between gap-4">
              <div className="space-y-1">
                <span className="mono-tag text-xs uppercase tracking-widest text-amber-500 block">
                  My Orders{me?.label ? ` — ${me.label}` : ""}
                </span>
                <p className="text-stone-500 text-xs">Payment status syncs from the exchange every few seconds.</p>
              </div>
              <button
                type="button"
                data-testid="shop-logout"
                onClick={() => { sessionStorage.removeItem("wallet_token"); setToken(""); setMe(null); setOrders([]); }}
                className="mono-tag text-[10px] uppercase tracking-widest text-stone-500 hover:text-amber-400 transition-colors"
              >
                Sign out
              </button>
            </div>
            {orders.length === 0 ? (
              <p data-testid="shop-orders-empty" className="text-stone-500 text-sm">No orders yet — pick a piece above.</p>
            ) : (
              <div className="space-y-2">
                {orders.map((o) => (
                  <div
                    key={o.order_id}
                    data-testid={`shop-order-${o.order_id}`}
                    className="rounded-lg border border-stone-800 bg-stone-900/60 px-4 py-3 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div>
                      <p className="text-sm text-stone-200">{o.item_name} <span className="text-stone-500">· {o.size}</span></p>
                      <p className="text-[11px] text-stone-500 mono-tag">{o.order_id} · {new Date(o.created_at).toLocaleString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="mono-tag text-xs text-amber-500">{fmtUsd(o.price_usd)}</span>
                      {o.status === "PAID" ? (
                        <span data-testid={`shop-order-${o.order_id}-status`} className="mono-tag text-[10px] uppercase tracking-widest text-emerald-400 border border-emerald-500/40 rounded px-2 py-1">
                          Paid on-chain
                        </span>
                      ) : (
                        <a
                          href={o.code ? `/wallet?pay=${o.code}` : "/wallet"}
                          data-testid={`shop-order-${o.order_id}-status`}
                          className="mono-tag text-[10px] uppercase tracking-widest text-amber-400 border border-amber-500/40 rounded px-2 py-1 hover:bg-amber-500 hover:text-stone-950 transition-colors"
                        >
                          Awaiting payment — pay now
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <footer className="border-t border-stone-800 mt-12">
        <div className="max-w-7xl mx-auto px-6 py-6 text-[11px] text-stone-500 mono-tag">
          BGNZN Apparel · settled in $sUSD on Chain 9940 · The Bourganization Institute
        </div>
      </footer>
    </div>
  );
}
